import { useMemo, useState } from 'react';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { useToast } from '../../../shared/components/Toast';
import { mensagemDeErro } from '../api';
import { ClientCoverage } from '../api/alocacoes';
import { useColaboradores } from '../hooks';
import { useAtualizarCobertura, useCoberturas } from '../hooks/alocacoes';
import { ACCESS_TASK_KIND_LABELS, ACCESS_TASK_STATUS_LABELS, formatDate } from './format';

/**
 * Lado do cliente de uma solicitação de férias (US-GP-015): por alocação,
 * substituto, ciência e conflitos do gestor do cliente e tarefas de acesso.
 */
export function CoberturaCliente({ requestId, canEdit, personId }: { requestId: string; canEdit: boolean; personId: string }) {
  const coberturas = useCoberturas(requestId);
  const lista = coberturas.data ?? [];
  if (lista.length === 0) return null;
  return (
    <div className="mt-2 space-y-2 rounded border border-gray-200 bg-gray-50 p-2">
      <p className="text-xs font-semibold uppercase text-gray-500">Clientes</p>
      {lista.map((c) => <Item key={c.id} c={c} canEdit={canEdit} personId={personId} />)}
    </div>
  );
}

function Item({ c, canEdit, personId }: { c: ClientCoverage; canEdit: boolean; personId: string }) {
  const toast = useToast();
  const atualizar = useAtualizarCobertura();
  const [editando, setEditando] = useState(false);

  async function salvar(payload: { needsSubstitute?: boolean; substituteUserId?: string | null }) {
    try {
      await atualizar.mutateAsync({ id: c.id, payload });
      toast.success('Cobertura atualizada.');
    } catch (err) {
      toast.error(mensagemDeErro(err, 'Não foi possível atualizar a cobertura.'));
    }
  }

  return (
    <div className="text-xs text-gray-700">
      <p className="font-medium text-gray-900">
        {c.clientName}{c.projectName ? ` · ${c.projectName}` : ''} <span className="font-normal text-gray-500">(gestor: {c.clientManagerName ?? '—'})</span>
      </p>
      <p>
        {c.acknowledgedAt ? `Ciência em ${new Date(c.acknowledgedAt).toLocaleString('pt-BR')} por ${c.acknowledgedByName ?? '—'}` : 'Sem ciência do cliente'}
        {' · '}{c.needsSubstitute ? (c.substitute ? `substituto: ${c.substitute.name}` : 'exige substituto, ainda não definido') : 'sem substituto'}
      </p>
      {c.feedback.filter((f) => f.kind === 'conflito').map((f, i) => (
        <p key={i} className="text-amber-800">Conflito ({new Date(f.createdAt).toLocaleDateString('pt-BR')}): {f.justification}</p>
      ))}
      {c.accessTasks.length > 0 && (
        <p>
          {c.accessTasks.map((t) => `${ACCESS_TASK_KIND_LABELS[t.kind]} em ${formatDate(t.dueDate)}: ${ACCESS_TASK_STATUS_LABELS[t.status].toLowerCase()}`).join(' · ')}
        </p>
      )}
      {canEdit && (
        <div className="mt-1 flex flex-wrap items-end gap-3">
          <label className="flex items-center gap-1">
            <input type="checkbox" checked={c.needsSubstitute} disabled={atualizar.isPending}
              onChange={(e) => salvar({ needsSubstitute: e.target.checked })} /> Exige substituto
          </label>
          {c.needsSubstitute && (editando ? (
            <SubstitutoSelect personId={personId} value={c.substitute?.id ?? ''}
              onChange={(v) => { setEditando(false); salvar({ substituteUserId: v || null }); }} />
          ) : (
            <button type="button" className="text-blue-700 hover:underline" onClick={() => setEditando(true)}>
              {c.substitute ? 'Trocar substituto' : 'Definir substituto'}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Montado só na edição, para não carregar a lista de colaboradores em cada cobertura. */
function SubstitutoSelect({ personId, value, onChange }: { personId: string; value: string; onChange: (v: string) => void }) {
  const colaboradores = useColaboradores({ limit: 200 });
  const opcoes = useMemo(
    () => (colaboradores.data?.data ?? []).filter((u) => u.id !== personId).map((u) => ({ value: u.id, label: u.name })),
    [colaboradores.data, personId],
  );
  return (
    <div className="w-64">
      <AutocompleteSelect label="Substituto" options={[{ value: '', label: 'Sem substituto' }, ...opcoes]} value={value} onChange={onChange} />
    </div>
  );
}
